import { useState } from 'react';
import { Database, Loader, CheckCircle, XCircle, FileText, Briefcase, Sparkles } from 'lucide-react';
import { seedJobsNow } from '../lib/seedJobsNow';
import { seedResumeTemplates } from '../lib/seedTemplates';
import { createSystemEmployer, seedJobsForDiscovery } from '../lib/seedJobsForDiscovery';
import { Navigation } from '../components/Navigation';
import { seed100Jobs } from '../lib/seed100Jobs';
import { getOrCreateSystemEmployer } from '../lib/systemEmployerService';

export function AdminSeed() {
  const [seeding, setSeeding] = useState(false);
  const [seedingTemplates, setSeedingTemplates] = useState(false);
  const [seedingDiscovery, setSeedingDiscovery] = useState(false);
  const [seeding100Jobs, setSeeding100Jobs] = useState(false);
  const [result, setResult] = useState<{ success: boolean; inserted?: number; errors?: number; error?: any } | null>(null);
  const [templateResult, setTemplateResult] = useState<{ success: boolean; message: string } | null>(null);
  const [discoveryResult, setDiscoveryResult] = useState<{ success: boolean; message: string; jobs?: any[] } | null>(null);
  const [jobs100Result, setJobs100Result] = useState<{ success: boolean; message: string; inserted?: number; errors?: number } | null>(null);

  const handleSeed = async () => {
    setSeeding(true);
    setResult(null);

    const seedResult = await seedJobsNow();
    setResult(seedResult);
    setSeeding(false);
  };

  const handleSeedTemplates = async () => {
    setSeedingTemplates(true);
    setTemplateResult(null);

    const result = await seedResumeTemplates();
    setTemplateResult(result);
    setSeedingTemplates(false);
  };

  const handleSeedDiscoveryJobs = async () => {
    setSeedingDiscovery(true);
    setDiscoveryResult(null);

    // First, create system employer
    const employerResult = await createSystemEmployer();

    if (!employerResult.success) {
      setDiscoveryResult({
        success: false,
        message: 'Failed to create system employer: ' + ((employerResult.error as any)?.message || 'Unknown error'),
      });
      setSeedingDiscovery(false);
      return;
    }

    // Then seed jobs
    const jobsResult = await seedJobsForDiscovery(employerResult.employerId!);

    if (jobsResult.success) {
      setDiscoveryResult({
        success: true,
        message: `Successfully seeded ${jobsResult.jobs?.length || 0} jobs for discovery section`,
        jobs: jobsResult.jobs,
      });
    } else {
      setDiscoveryResult({
        success: false,
        message: 'Failed to seed discovery jobs: ' + ((jobsResult.error as any)?.message || 'Unknown error'),
      });
    }

    setSeedingDiscovery(false);
  };

  const handleSeed100Jobs = async () => {
    setSeeding100Jobs(true);
    setJobs100Result(null);

    const systemEmployerId = await getOrCreateSystemEmployer();

    if (!systemEmployerId) {
      setJobs100Result({
        success: false,
        message: 'Failed to get or create system employer',
      });
      setSeeding100Jobs(false);
      return;
    }

    const result = await seed100Jobs(systemEmployerId);

    setJobs100Result({
      success: result.success,
      message: result.message,
      inserted: result.inserted,
      errors: result.errors,
    });

    setSeeding100Jobs(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200">
          <div className="text-center mb-8">
            <Database className="w-16 h-16 text-cyan-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Database Seeder</h1>
            <p className="text-gray-600">Add test data to the database</p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Seed Test Jobs:</h2>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 font-bold">•</span>
                  <span>6 sample jobs from major job boards (Indeed, LinkedIn, Glassdoor, ZipRecruiter, Monster)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 font-bold">•</span>
                  <span>All jobs use ResuSwipe test company names</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 font-bold">•</span>
                  <span>Jobs include titles, descriptions, salaries, locations, and required skills</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 font-bold">•</span>
                  <span>All jobs are marked as active and searchable</span>
                </li>
              </ul>
            </div>

            <button
              onClick={handleSeed}
              disabled={seeding}
              className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {seeding ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  Seeding Database...
                </>
              ) : (
                <>
                  <Database className="w-6 h-6" />
                  Seed Test Jobs
                </>
              )}
            </button>

            {result && (
              <div className={`rounded-xl p-6 border-2 ${
                result.success
                  ? 'bg-green-50 border-green-500'
                  : 'bg-red-50 border-red-500'
              }`}>
                <div className="flex items-start gap-3">
                  {result.success ? (
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h3 className={`font-bold text-lg mb-2 ${
                      result.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {result.success ? 'Job Seeding Successful!' : 'Job Seeding Failed'}
                    </h3>
                    {result.success && (
                      <p className="text-green-800 text-sm">
                        Successfully inserted {result.inserted || 0} jobs into the database.
                        {result.errors ? ` ${result.errors} jobs were skipped (likely already exist).` : ''}
                      </p>
                    )}
                    {!result.success && (
                      <p className="text-red-800 text-sm">
                        {result.error?.message || 'An error occurred while seeding the database.'}
                      </p>
                    )}
                    <p className="text-gray-700 text-sm mt-3">
                      You can now search for jobs in the AI Career Assistant and Discover page.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t-2 border-gray-200 pt-6 mt-6">
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200 mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-orange-500" />
                  Seed 100 Jobs (Recommended):
                </h2>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>100 diverse jobs across 9 industries (Technology, Healthcare, Finance, Education, Retail, Manufacturing, Hospitality, Marketing, Sales, Construction)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>Evenly distributed with multiple experience levels (Entry, Mid, Senior, Executive)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>Mix of remote, hybrid, and on-site positions with realistic salaries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>Uses ResuSwipe System employer account for platform-wide demo jobs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>Automatically runs on first visit if no jobs exist</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleSeed100Jobs}
                disabled={seeding100Jobs}
                className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg"
              >
                {seeding100Jobs ? (
                  <>
                    <Loader className="w-6 h-6 animate-spin" />
                    Seeding 100 Jobs...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-6 h-6" />
                    Seed 100 Jobs
                  </>
                )}
              </button>

              {jobs100Result && (
                <div className={`rounded-xl p-6 border-2 mt-4 ${
                  jobs100Result.success
                    ? 'bg-green-50 border-green-500'
                    : 'bg-red-50 border-red-500'
                }`}>
                  <div className="flex items-start gap-3">
                    {jobs100Result.success ? (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg mb-2 ${
                        jobs100Result.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {jobs100Result.success ? '100 Jobs Seeded Successfully!' : 'Job Seeding Failed'}
                      </h3>
                      <p className={jobs100Result.success ? 'text-green-800 text-sm' : 'text-red-800 text-sm'}>
                        {jobs100Result.message}
                      </p>
                      {jobs100Result.success && (
                        <>
                          <p className="text-green-800 text-sm mt-2">
                            Successfully inserted {jobs100Result.inserted || 0} jobs.
                            {jobs100Result.errors ? ` ${jobs100Result.errors} jobs were skipped (already exist).` : ''}
                          </p>
                          <p className="text-gray-700 text-sm mt-3">
                            Jobs are now available in the Discover/Swipe section for all job seekers.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t-2 border-gray-200 pt-6 mt-6">
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200 mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Seed Discovery Jobs:</h2>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">•</span>
                    <span>15 diverse jobs across multiple industries (Healthcare, Finance, Education, Tech, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">•</span>
                    <span>Jobs for various experience levels (Entry, Mid, Senior)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">•</span>
                    <span>Mix of remote, hybrid, and on-site positions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">•</span>
                    <span>Creates a system employer profile for demo jobs</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleSeedDiscoveryJobs}
                disabled={seedingDiscovery}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {seedingDiscovery ? (
                  <>
                    <Loader className="w-6 h-6 animate-spin" />
                    Seeding Discovery Jobs...
                  </>
                ) : (
                  <>
                    <Briefcase className="w-6 h-6" />
                    Seed Discovery Jobs
                  </>
                )}
              </button>

              {discoveryResult && (
                <div className={`rounded-xl p-6 border-2 mt-4 ${
                  discoveryResult.success
                    ? 'bg-green-50 border-green-500'
                    : 'bg-red-50 border-red-500'
                }`}>
                  <div className="flex items-start gap-3">
                    {discoveryResult.success ? (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg mb-2 ${
                        discoveryResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {discoveryResult.success ? 'Discovery Jobs Seeded!' : 'Discovery Job Seeding Failed'}
                      </h3>
                      <p className={discoveryResult.success ? 'text-green-800 text-sm' : 'text-red-800 text-sm'}>
                        {discoveryResult.message}
                      </p>
                      {discoveryResult.success && (
                        <p className="text-gray-700 text-sm mt-3">
                          Jobs are now available in the Discover/Swipe section for job seekers.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t-2 border-gray-200 pt-6 mt-6">
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200 mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Seed Resume Templates:</h2>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>5 professional resume templates (Classic, Modern, Executive, Technical, Creative)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>All templates are ATS-optimized with scores 92-98%</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>Templates include color schemes, fonts, and layout configurations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    <span>Industry tags for Technology, Business, Finance, Healthcare, and more</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleSeedTemplates}
                disabled={seedingTemplates}
                className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {seedingTemplates ? (
                  <>
                    <Loader className="w-6 h-6 animate-spin" />
                    Seeding Templates...
                  </>
                ) : (
                  <>
                    <FileText className="w-6 h-6" />
                    Seed Resume Templates
                  </>
                )}
              </button>

              {templateResult && (
                <div className={`rounded-xl p-6 border-2 mt-4 ${
                  templateResult.success
                    ? 'bg-green-50 border-green-500'
                    : 'bg-red-50 border-red-500'
                }`}>
                  <div className="flex items-start gap-3">
                    {templateResult.success ? (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h3 className={`font-bold text-lg mb-2 ${
                        templateResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {templateResult.success ? 'Template Seeding Successful!' : 'Template Seeding Failed'}
                      </h3>
                      <p className={templateResult.success ? 'text-green-800 text-sm' : 'text-red-800 text-sm'}>
                        {templateResult.message}
                      </p>
                      {templateResult.success && (
                        <p className="text-gray-700 text-sm mt-3">
                          Visit the Templates page to browse and select professional resume templates.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
